// Radha — application root.
// Auth gate: unauthenticated users see Login; authenticated users get the shell.
// Everything is wrapped in the TanStack QueryClientProvider so the module hooks
// work anywhere in the tree.
import React from "react";
import { QueryClientProvider } from "@tanstack/react-query";

import { queryClient } from "./lib/queryClient";
import { useAuth } from "./lib/auth";
import AppShell from "./components/AppShell";
import Login from "./components/Login";
import DirectShell from "./pages/DirectShell";
import PortalPage from "./pages/PortalPage";

// The direct-Supabase app IS the app now: one public URL, one Supabase login, a
// tab per module (see DirectShell). It is the default, so a plain URL lands
// straight in — the Nikunj workflow.
//
// The older gateway app (FastAPI-backed, RS256 login) is kept reachable at
// ?gateway=1 for the module capabilities that still need a service — AI
// proposals, GST IRP, webhooks — but it is no longer the front door.
function isGateway(): boolean {
  return new URLSearchParams(window.location.search).has("gateway");
}

// A customer's share link (?portal=<token>) — public, no Supabase Auth session,
// resolved entirely by the portal-statement Edge Function. Checked before the
// auth gate so a customer is never asked to log in.
function portalToken(): string | null {
  return new URLSearchParams(window.location.search).get("portal");
}

export default function App() {
  const { claims, isAuthenticated, login, logout } = useAuth();

  const portal = portalToken();
  if (portal) {
    return (
      <QueryClientProvider client={queryClient}>
        <PortalPage token={portal} />
      </QueryClientProvider>
    );
  }

  if (isGateway()) {
    return (
      <QueryClientProvider client={queryClient}>
        {isAuthenticated
          ? <AppShell claims={claims} onLogout={logout} />
          : <Login onLogin={login} />}
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <DirectShell />
    </QueryClientProvider>
  );
}
